import { BUILD_AI_AGENTS_WEBINAR_REGISTRATION_HREF } from '~/utils/build-ai-agents-webinar-atf-slot';

export type ExperienceAtfCardAction = { type: 'link'; href: string } | { type: 'video'; videoId: string };

/**
 * Matches ATF promo templates in Figma (node 7374:9689 + 7633:25654 + 7665:32868).
 */
export type ExperienceAtfPromoVariant =
  | 'agentic_ai'
  | 'build_ai_agents_webinar'
  | 'flash_sale'
  | 'free'
  | 'marty_cagan_webinar'
  | 'masters'
  | 'mba';

export type ExperienceAtfCardConfig = {
  id: string;
  action: ExperienceAtfCardAction;
  ctaLabel: string;
  description: string;
  /** Omitted by campaigns whose design drops the eyebrow row entirely. */
  eyebrow?: string;
  promoVariant: ExperienceAtfPromoVariant;
  /** Primary heading (H2 / tests). */
  title: string;
  /** Optional second line (Agentic card). */
  titleLine2?: string;
  /** Visual configuration shared by time-boxed flash-sale cards. */
  flashSale?: {
    backgroundGradient: string;
    backgroundImage: string;
    backgroundImageBlendMode?: 'normal' | 'overlay' | 'screen' | 'soft-light';
    backgroundImageObjectPosition?: string;
    backgroundImageOpacity?: number;
    bodyColor: string;
    eyebrowColor: string;
    stampImageDesktop?: string;
    stampImageMobile?: string;
  };
};

/** Static art for ATF promos (`public/images/experiments/ab_experience_atf_2026/`). */
export const ATF_PROMO_ASSETS = {
  agenticAiBg: '/images/experiments/ab_experience_atf_2026/agentic-ai-bg-v2.png',
  buildAiAgentsWebinarPortrait:
    '/images/experiments/ab_experience_atf_2026/peter-kowalchuk-build-ai-agents-webinar.jpg',
  martyCaganPortrait: '/images/experiments/ab_experience_atf_2026/marty-cagan-webinar-portrait.png',
  martyCaganWaveBg: '/images/experiments/ab_experience_atf_2026/marty-cagan-webinar-wave-bg.png',
  mbaBg: '/images/experiments/ab_experience_atf_2026/mba-bg.png',
  schoolsGrid: '/images/experiments/ab_experience_atf_2026/schools-grid.png',
} as const;

/**
 * Intrinsic pixel size of each file (must match what is on disk).
 * MBA art is exported @2x for a 300×375 CSS card; schools grid is still @1x until re-exported.
 */
export const ATF_PROMO_ASSET_INTRINSIC_PX = {
  buildAiAgentsWebinarPortrait: { height: 280, width: 232 },
  martyCaganPortrait: { height: 280, width: 232 },
  martyCaganWaveBg: { height: 750, width: 600 },
  mbaBg: { height: 750, width: 600 },
  schoolsGrid: { height: 158, width: 205 },
} as const;

/** Desktop promo shell (Figma 7374:9689). */
export const EXPERIENCE_ATF_CARD_SHELL_WIDTH_PX = 300;
export const EXPERIENCE_ATF_CARD_SHELL_HEIGHT_PX = 375;

/**
 * Mobile promo shell (Figma 7382:11004 — node 7382:11940 frame).
 * Outer slide width keeps a 5px inset each side, same as desktop.
 */
export const EXPERIENCE_ATF_CARD_SHELL_WIDTH_PX_MOBILE = 260;
export const EXPERIENCE_ATF_CARD_SHELL_HEIGHT_PX_MOBILE = 325;

/** Swiper slide / static SSR row cell width (px); matches card outer width. */
export const EXPERIENCE_ATF_SLIDE_WIDTH_PX = 310;
export const EXPERIENCE_ATF_SLIDE_WIDTH_PX_MOBILE = 270;

/**
 * Min height for the carousel row (avoids CLS). Shell height + vertical inset (desktop 375+13, mobile 325+13).
 */
export const EXPERIENCE_ATF_CAROUSEL_MIN_HEIGHT_PX = 388;
export const EXPERIENCE_ATF_CAROUSEL_MIN_HEIGHT_PX_MOBILE = 338;

/**
 * Marty Cagan replay — default slot 4 on /experience ATF (Figma 7665:32868).
 */
export const EXPERIENCE_ATF_MARTY_CAGAN_WEBINAR_CARD: ExperienceAtfCardConfig = {
  id: 'marty_cagan_webinar_apr2026',
  promoVariant: 'marty_cagan_webinar',
  eyebrow: 'webinar',
  title: 'The new standard for PMs with Marty Cagan',
  description: 'Build, lead, and grow products in the age of AI',
  ctaLabel: 'Watch Now',
  action: { type: 'link', href: '/video/new-standard-for-product-managers-replay' },
};

/**
 * Build AI Agents live webinar — replaces slot 4 while `isBuildAiAgentsWebinarAtfSlotActive` is true
 * (Figma 888:27846, CONENG-2426).
 */
export const EXPERIENCE_ATF_BUILD_AI_AGENTS_WEBINAR_CARD: ExperienceAtfCardConfig = {
  id: 'build_ai_agents_webinar_jun2026',
  promoVariant: 'build_ai_agents_webinar',
  eyebrow: 'webinar',
  title: 'Live build: AI agents',
  titleLine2: 'from scratch',
  description: 'How AI agents work under the hood',
  ctaLabel: 'Register For Free!',
  action: { type: 'link', href: BUILD_AI_AGENTS_WEBINAR_REGISTRATION_HREF },
};

/**
 * Promo order (agentic → free → masters → marty_cagan_webinar). Agentic leads by default after
 * the free-trial experiment ended.
 */
export const EXPERIENCE_ATF_CARDS: ExperienceAtfCardConfig[] = [
  {
    /**
     * Default-path Agentic AI promo. After the `agentic-ai-ft-2026` free-trial experiment
     * ended, the free-trial visual shell was promoted as the OG `agentic` look (palette,
     * decoration, rocket CTA chrome); flag-on traffic still prepends `FREE_TRIAL_ATF_CARD`
     * but reuses the same OG variant + `ATF_agentic` analytics.
     */
    id: 'agentic_ai',
    promoVariant: 'agentic_ai',
    eyebrow: 'Most popular',
    title: 'Build AI systems',
    titleLine2: 'that act.',
    description: 'Every agentic AI skill, in one place.',
    ctaLabel: 'Enroll Now',
    action: { type: 'link', href: '/course/agentic-ai--nd900' },
  },
  {
    id: 'free_courses',
    promoVariant: 'free',
    eyebrow: 'Free programs',
    title: 'Start building AI skills for free',
    description: 'Explore top-rated courses and start today.',
    ctaLabel: 'Start Free Today',
    action: { type: 'link', href: '/catalog?price=free' },
  },
  {
    id: 'masters_ai',
    promoVariant: 'masters',
    eyebrow: 'New at Udacity',
    title: "Advance your career with a Master's in AI",
    description: 'Flexible, affordable, and fully accredited.',
    ctaLabel: 'Explore MSc in AI',
    action: { type: 'link', href: '/masters-artificial-intelligence' },
  },
  EXPERIENCE_ATF_MARTY_CAGAN_WEBINAR_CARD,
];

/**
 * First-position promo tile shown on the `agentic-ai-ft-2026=on` flag-on /experience page
 * (Figma 115:22190). Shares the OG `agentic` visual + analytics now that the free-trial
 * design was promoted to the default Agentic tile.
 */
export const FREE_TRIAL_ATF_CARD: ExperienceAtfCardConfig = {
  id: 'agentic_ai_free_trial',
  promoVariant: 'agentic_ai',
  eyebrow: 'Most popular',
  title: 'Build AI systems',
  titleLine2: 'that act.',
  description: 'Every agentic AI skill, in one place.',
  ctaLabel: 'Try For Free',
  action: {
    type: 'link',
    href: '/course/agentic-ai--nd900',
  },
};

export const EXPERIMENT_KEY = 'ab_experience_atf_2026';
